/**
 * Input primitive — Cohere 2026 design system.
 *
 * Four variants (text, textarea, search, number) styled exclusively via
 * CSS custom-property references. Label associates via htmlFor/id.
 * Error state renders errorText below in error colour and applies
 * aria-invalid + aria-describedby for accessibility.
 *
 * @see specs/integration-plan.md Section 3.5
 */
import React, { useId, useRef, useState, useCallback, useEffect } from "react";
import type { CSSProperties } from "react";
import { Icon, type IconName } from "./Icon";

export type InputVariant = "text" | "textarea" | "search" | "number";

export interface InputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>,
    "type"
  > {
  variant?: InputVariant;
  label?: string;
  helperText?: string;
  errorText?: string;
  leadingIcon?: IconName;
  onClear?: () => void; // for search variant
}

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */

const LABEL_STYLE: CSSProperties = {
  display: "block",
  fontSize: "var(--fs-caption)",
  lineHeight: 1.4,
  fontFamily: "var(--font-ui)",
  color: "var(--fg3)",
  marginBottom: "var(--space-xs)",
};

const BASE_INPUT_STYLE: CSSProperties = {
  display: "block",
  width: "100%",
  fontSize: "var(--fs-body)",
  lineHeight: 1.5,
  fontFamily: "var(--font-ui)",
  color: "var(--fg1)",
  backgroundColor: "var(--bg1)",
  border: "1px solid var(--color-hairline, var(--border-default))",
  borderRadius: "var(--radius-xs)",
  padding: "var(--space-md) var(--space-lg)",
  outline: "none",
  transition: "border-color 0.15s ease",
  boxSizing: "border-box" as const,
};

const ERROR_INPUT_STYLE: CSSProperties = {
  borderColor: "var(--color-error)",
};

const FOCUS_INPUT_STYLE: CSSProperties = {
  borderColor: "var(--color-form-focus, var(--color-action-blue))",
};

const HELPER_STYLE: CSSProperties = {
  fontSize: "var(--fs-micro)",
  lineHeight: 1.4,
  fontFamily: "var(--font-ui)",
  color: "var(--fg3)",
  marginTop: "var(--space-xxs)",
};

const ERROR_TEXT_STYLE: CSSProperties = {
  fontSize: "var(--fs-micro)",
  lineHeight: 1.4,
  fontFamily: "var(--font-ui)",
  color: "var(--color-error)",
  marginTop: "var(--space-xxs)",
};

const WRAPPER_STYLE: CSSProperties = {
  position: "relative",
  display: "flex",
  alignItems: "center",
};

const CLEAR_BUTTON_STYLE: CSSProperties = {
  position: "absolute",
  right: "var(--space-sm)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: "none",
  border: "none",
  padding: "var(--space-xxs)",
  cursor: "pointer",
  color: "var(--fg3)",
  borderRadius: "var(--radius-xs)",
};

/* ------------------------------------------------------------------ */
/*  Textarea auto-resize helper                                        */
/* ------------------------------------------------------------------ */

const MAX_TEXTAREA_LINES = 12;
const LINE_HEIGHT_PX = 24; // 16px * 1.5
const PADDING_Y_PX = 24; // ~12px top + 12px bottom (--space-md * 2)

function autoResizeTextarea(el: HTMLTextAreaElement) {
  el.style.height = "auto";
  const maxHeight = MAX_TEXTAREA_LINES * LINE_HEIGHT_PX + PADDING_Y_PX;
  el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
  el.style.overflow = el.scrollHeight > maxHeight ? "auto" : "hidden";
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function Input({
  variant = "text",
  label,
  helperText,
  errorText,
  leadingIcon,
  onClear,
  id: providedId,
  className,
  style: userStyle,
  disabled,
  value,
  defaultValue,
  onChange,
  onFocus: userOnFocus,
  onBlur: userOnBlur,
  ...rest
}: InputProps) {
  const autoId = useId();
  const fieldId = providedId ?? autoId;
  const errorId = `${fieldId}-error`;
  const helperId = `${fieldId}-helper`;
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [focused, setFocused] = useState(false);

  const hasError = Boolean(errorText);
  const hasHelper = Boolean(helperText) && !hasError;

  // Compute describedby
  const describedBy = hasError
    ? errorId
    : hasHelper
      ? helperId
      : undefined;

  // Focus handlers
  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFocused(true);
      if (userOnFocus) {
        (userOnFocus as React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>)(e);
      }
    },
    [userOnFocus],
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFocused(false);
      if (userOnBlur) {
        (userOnBlur as React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>)(e);
      }
    },
    [userOnBlur],
  );

  // Textarea auto-resize on value change
  const handleTextareaChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      autoResizeTextarea(e.target);
      if (onChange) {
        (onChange as React.ChangeEventHandler<HTMLTextAreaElement>)(e);
      }
    },
    [onChange],
  );

  // Auto-resize on mount and when value prop changes (controlled)
  useEffect(() => {
    if (variant === "textarea" && textareaRef.current) {
      autoResizeTextarea(textareaRef.current);
    }
  }, [variant, value]);

  // Determine whether search clear button should show
  const showClear =
    variant === "search" &&
    onClear &&
    typeof value === "string" &&
    value.length > 0;

  // Should we show a leading icon?
  const resolvedLeadingIcon: IconName | undefined =
    variant === "search"
      ? (leadingIcon ?? "search")
      : leadingIcon;

  // Compute input style
  const computedInputStyle: CSSProperties = {
    ...BASE_INPUT_STYLE,
    ...(hasError ? ERROR_INPUT_STYLE : undefined),
    ...(focused && !hasError ? FOCUS_INPUT_STYLE : undefined),
    ...(resolvedLeadingIcon
      ? { paddingLeft: "calc(var(--space-lg) + 16px + var(--space-xs))" }
      : undefined),
    ...(showClear
      ? { paddingRight: "calc(var(--space-lg) + 16px + var(--space-xs))" }
      : undefined),
    ...(disabled ? { opacity: 0.5, cursor: "not-allowed" } : undefined),
    ...userStyle,
  };

  // CSS classes for focus/error state (enables test assertions)
  const wrapperClasses = [
    "input-wrapper",
    focused ? "input-wrapper--focused" : "",
    hasError ? "input-wrapper--error" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const inputClasses = [
    "input-field",
    focused ? "input-field--focused" : "",
    hasError ? "input-field--error" : "",
  ]
    .filter(Boolean)
    .join(" ");

  // Shared props for input / textarea
  const sharedProps = {
    id: fieldId,
    className: inputClasses,
    style: computedInputStyle,
    disabled,
    "aria-invalid": hasError ? ("true" as const) : undefined,
    "aria-describedby": describedBy,
    onFocus: handleFocus,
    onBlur: handleBlur,
  };

  // Render the field element
  let fieldElement: React.ReactNode;

  if (variant === "textarea") {
    fieldElement = (
      <textarea
        ref={textareaRef}
        {...sharedProps}
        value={value}
        defaultValue={defaultValue}
        onChange={handleTextareaChange}
        rows={1}
        style={{
          ...computedInputStyle,
          resize: "none",
          minHeight: `${LINE_HEIGHT_PX + PADDING_Y_PX}px`,
        }}
        {...(rest as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
      />
    );
  } else {
    const inputType =
      variant === "search" ? "text" : variant === "number" ? "number" : "text";

    fieldElement = (
      <input
        type={inputType}
        {...sharedProps}
        value={value}
        defaultValue={defaultValue}
        onChange={
          onChange as React.ChangeEventHandler<HTMLInputElement> | undefined
        }
        {...(rest as React.InputHTMLAttributes<HTMLInputElement>)}
      />
    );
  }

  return (
    <div
      className={`input-root${variant === "number" ? " input-root--number" : ""}`}
    >
      {/* Label */}
      {label && (
        <label htmlFor={fieldId} style={LABEL_STYLE}>
          {label}
        </label>
      )}

      {/* Input wrapper (for icon positioning) */}
      <div className={wrapperClasses} style={WRAPPER_STYLE}>
        {/* Leading icon */}
        {resolvedLeadingIcon && (
          <span
            style={{
              position: "absolute",
              left: "var(--space-lg)",
              display: "inline-flex",
              alignItems: "center",
              pointerEvents: "none",
              color: "var(--fg3)",
            }}
          >
            <Icon name={resolvedLeadingIcon} size={16} />
          </span>
        )}

        {fieldElement}

        {/* Clear button for search variant */}
        {showClear && (
          <button
            type="button"
            aria-label="Clear"
            data-clear
            onClick={onClear}
            style={CLEAR_BUTTON_STYLE}
          >
            <Icon name="x" size={16} />
          </button>
        )}
      </div>

      {/* Error text (takes precedence over helper text) */}
      {hasError && (
        <div id={errorId} className="input-error-text" style={ERROR_TEXT_STYLE}>
          {errorText}
        </div>
      )}

      {/* Helper text (only when no error) */}
      {hasHelper && (
        <div id={helperId} className="input-helper-text" style={HELPER_STYLE}>
          {helperText}
        </div>
      )}

      {/* Number variant stepper hide-until-hover CSS (injected inline) */}
      {variant === "number" && (
        <style>{`
          .input-root--number input[type="number"]::-webkit-inner-spin-button,
          .input-root--number input[type="number"]::-webkit-outer-spin-button {
            opacity: 0;
            transition: opacity 0.15s ease;
          }
          .input-root--number:hover input[type="number"]::-webkit-inner-spin-button,
          .input-root--number:hover input[type="number"]::-webkit-outer-spin-button {
            opacity: 1;
          }
        `}</style>
      )}
    </div>
  );
}

export default Input;
